import { Link } from '@tanstack/react-router'
import { ArrowLeft, BookOpen, CircleAlert, UserCheck } from 'lucide-react'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { Tooltip } from '@/components/ui/Tooltip'
import { formatKorteDatum, formatUren } from '@/lib/format'
import { useSemesterKeuze } from '@/lib/semester-context'
import { useVak } from '@/lib/vak-api'
import type { VakGroepDetail, VakMedewerker } from '@/lib/vak-types'

export function VakPage({ naam }: { naam: string }) {
  const { semesterId, semester, isLoading: semesterLaadt } = useSemesterKeuze()
  const query = useVak(semesterId, naam)

  const laadt = semesterLaadt || query.isLoading
  const vak = query.data

  return (
    <div className="space-y-6">
      <PageHeader
        title={vak?.naam ?? naam}
        description={
          vak ? (
            <span>
              {[
                `${vak.aantalGroepen} ${vak.aantalGroepen === 1 ? 'groep' : 'groepen'}`,
                `${vak.lesweken} lesweken`,
                `${formatUren(vak.uren)} u`,
                semester?.naam,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          ) : null
        }
        actions={
          <Link to="/vakken" className="btn-ghost">
            <ArrowLeft className="h-4 w-4" />
            Terug naar vakken
          </Link>
        }
      />

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : laadt || !vak ? (
        <div className="space-y-4">
          <Skeleton className="h-[86px] w-full rounded-card" />
          <Skeleton className="h-64 w-full rounded-card" />
          <Skeleton className="h-64 w-full rounded-card" />
        </div>
      ) : vak.groepen.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="Geen groepen in dit semester"
          description="Dit vak heeft dit semester bij geen enkele groep lesweken."
        />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start">
          <div className="space-y-4">
            {vak.groepen.map((groep) => (
              <Groepblok key={groep.lesreeksId} groep={groep} />
            ))}
          </div>

          <Medewerkerlijst medewerkers={vak.medewerkers} totaal={vak.uren} />
        </div>
      )}
    </div>
  )
}

function Groepblok({ groep }: { groep: VakGroepDetail }) {
  const zonderDocent = groep.lesmomenten.filter(
    (lesmoment) => lesmoment.medewerkerNaam === null,
  ).length

  return (
    <section className="overflow-hidden rounded-card bg-surface shadow-card">
      <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-hairline px-5 py-4">
        <div className="min-w-0">
          <Link
            to="/groep/$code"
            params={{ code: groep.code }}
            className="text-sm font-semibold text-ink hover:underline"
          >
            {groep.code}
          </Link>
          <p className="mt-0.5 text-xs text-ink-faint">
            {[
              groep.trajectLabel,
              `${groep.lesweken} lesweken in dit semester`,
              `${groep.lesmomenten.length} ${groep.lesmomenten.length === 1 ? 'lesmoment' : 'lesmomenten'}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>

        <span className="num text-sm font-semibold text-ink">{formatUren(groep.uren)} u</span>
      </div>

      <ul className="divide-y divide-hairline">
        {groep.lesmomenten.length === 0 ? (
          <li className="px-5 py-3 text-sm text-ink-faint">
            Geen lesmomenten. Zonder lesmoment levert deze reeks geen uren op.
          </li>
        ) : (
          groep.lesmomenten.map((lesmoment) => (
            <li
              key={`${lesmoment.dag}-${lesmoment.dagdeel}-${lesmoment.medewerkerNaam ?? ''}`}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-5 py-2.5 text-sm"
            >
              <span className="min-w-[9rem] text-ink">
                {lesmoment.dagLabel} {lesmoment.dagdeel}
              </span>
              <span
                className={
                  lesmoment.medewerkerNaam ? 'text-ink-soft' : 'text-status-onderbezet font-medium'
                }
              >
                {lesmoment.medewerkerNaam ?? 'Nog te verdelen'}
              </span>
              {lesmoment.isMentor ? (
                <span className="inline-flex items-center gap-1 text-xs text-ink-faint">
                  <UserCheck className="h-3.5 w-3.5" strokeWidth={1.85} />
                  Mentor
                </span>
              ) : null}
            </li>
          ))
        )}
      </ul>

      {groep.niveaus.length > 0 || zonderDocent > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-hairline bg-fill-1 px-5 py-3">
          {groep.niveaus.map((fase) => (
            <Tooltip
              key={`${fase.niveau}-${fase.vanaf}`}
              content={`${formatKorteDatum(fase.vanaf)} tot ${formatKorteDatum(fase.tot)}`}
            >
              <span className="rounded-full bg-surface px-2.5 py-1 text-xs text-ink-soft shadow-card">
                {fase.niveau} · {fase.weken} wk
              </span>
            </Tooltip>
          ))}
          {zonderDocent > 0 ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-status-onderbezet">
              <CircleAlert className="h-3.5 w-3.5" strokeWidth={1.85} />
              {zonderDocent} zonder docent
            </span>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

interface MedewerkerlijstProps {
  medewerkers: VakMedewerker[]
  totaal: number
}

function Medewerkerlijst({ medewerkers, totaal }: MedewerkerlijstProps) {
  const toegewezen = medewerkers.reduce((som, medewerker) => som + medewerker.uren, 0)
  const rest = Math.round((totaal - toegewezen) * 100) / 100

  return (
    <section className="overflow-hidden rounded-card bg-surface shadow-card">
      <div className="border-b border-hairline px-5 py-4">
        <h2 className="text-sm font-semibold text-ink">Medewerkers</h2>
        <p className="mt-0.5 text-xs text-ink-faint">De lesuren van dit vak in dit semester.</p>
      </div>

      {medewerkers.length === 0 ? (
        <p className="px-5 py-4 text-sm text-ink-faint">Nog niemand toegewezen.</p>
      ) : (
        <ul className="divide-y divide-hairline">
          {medewerkers.map((medewerker) => (
            <li key={medewerker.medewerkerId} className="px-5 py-3">
              <span className="flex items-baseline justify-between gap-3">
                <Link
                  to="/medewerker/$id"
                  params={{ id: medewerker.medewerkerId }}
                  className="min-w-0 truncate text-sm text-ink hover:underline"
                >
                  {medewerker.naam}
                </Link>
                <span className="num shrink-0 text-sm text-ink-soft">
                  {formatUren(medewerker.uren)} u
                </span>
              </span>
              <span className="mt-0.5 block truncate text-xs text-ink-faint">
                {medewerker.groepen.join(', ')}
              </span>
            </li>
          ))}
        </ul>
      )}

      {rest > 0 ? (
        <p className="border-t border-hairline px-5 py-3 text-xs font-medium text-status-onderbezet">
          {formatUren(rest)} uur staat op niemand.
        </p>
      ) : null}
    </section>
  )
}
